import { FC } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';

import { useAuth } from '@clerk/clerk-react';
import { observer } from 'mobx-react-lite';

import ProtectedRoute from '../components/ProtectedRoute';
import AcceptOrgInvitation from '../pages/AcceptOrgInvitation';
import { AccountPreferences } from '../pages/AccountPreferences/AccountPreferences';
import Campaign from '../pages/Campaign';
import Campaigns from '../pages/Campaigns';
import { CreateAccount } from '../pages/CreateAccount';
import { CreateOrganization } from '../pages/CreateOrganization';
import Crew from '../pages/Crew';
import CrewSharedSessions from '../pages/CrewSharedSessions';
import Dashboard from '../pages/Dashboard';
import DashboardBeta from '../pages/DashboardBeta';
import DeviceDetails from '../pages/DeviceDetails';
import Devices from '../pages/Devices';
import DeviceList from '../pages/Devices/DeviceList';
import DeviceTypeList from '../pages/DeviceTypes/DeviceTypeList';
import DualCrew from '../pages/DualCrew';
import InvitePage from '../pages/InvitePage';
import Locations from '../pages/Locations';
import Login from '../pages/Login';
import LoginClerk from '../pages/LoginClerk';
import Logout from '../pages/Logout';
import MissionPlanner from '../pages/MissionPlanner';
import NotFound from '../pages/NotFound';
import OrganizationList from '../pages/OrganizationList';
import OrganizationSettings from '../pages/OrganizationSettings';
import RegisterDevice from '../pages/RegisterDevice';
import { ResetPassword } from '../pages/ResetPassword/ResetPassword';
import { Signup } from '../pages/Signup';
import SignupClerk from '../pages/SignupClerk';
import UsersPermissions from '../pages/UserPermissions';
import VerifyEmail from '../pages/VerifyEmail';
import VersionInfo from '../pages/VersionInfo';
import { pilotRoutes } from '../pilot/routes';
import sessionStore from '../stores/SessionStore';

const RootGate = observer(() => {
  const { isLoaded, isSignedIn } = useAuth();

  return sessionStore.isAuthenticated ? (
    <Navigate replace to='/dashboard' />
  ) : isLoaded && isSignedIn ? (
    <Navigate replace to='/login' />
  ) : (
    <Navigate replace to='/login' />
  );
});

const routes = [
  {
    path: '/',
    element: <RootGate />
  },
  {
    path: '/legacy-login',
    element: <Login />
  },
  {
    path: '/login/*',
    element: <LoginClerk />
  },
  {
    path: '/signup/*',
    element: <SignupClerk />
  },
  {
    path: '/legacy-signup',
    element: <Signup />
  },
  {
    path: '/reset-password',
    element: <ResetPassword />
  },
  {
    path: '/verify-email/:token',
    element: <VerifyEmail />
  },
  {
    path: '/accept-invitation/:token',
    element: <AcceptOrgInvitation />
  },
  {
    path: '/invite-page',
    element: <InvitePage />
  },
  {
    path: '/create-account',
    element: <CreateAccount />
  },
  {
    path: '/version-info',
    element: <VersionInfo />
  },
  {
    path: '/organization-list',
    element: <OrganizationList />
  },

  {
    path: '/crew/:sessionId/device/:deviceUuid',
    element: <Crew />
  },
  {
    path: '/crew-dual/:sessionId/device/:droneUuid',
    element: <DualCrew />
  },
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        path: '/locations',
        element: <Locations />
      },
      {
        path: '/dashboard',
        element: <DashboardBeta />
      },
      {
        path: '/home',
        element: <Navigate replace to='/dashboard' />
      },
      {
        path: '/dashboard-beta',
        element: <Dashboard />
      },
      {
        path: '/logout',
        element: <Logout />
      },
      {
        path: '/account-preferences',
        element: <AccountPreferences />
      },
      {
        path: '/user-permissions',
        element: <UsersPermissions />
      },

      {
        path: '/create-organization',
        element: <CreateOrganization />
      },
      {
        path: '/crew',
        element: <CrewSharedSessions />
      },
      {
        path: '/register-device/:id',
        element: <RegisterDevice />
      },
      {
        path: '/register-device',
        element: <RegisterDevice />
      },

      {
        path: '/campaigns',
        element: <Campaigns />
      },
      {
        path: '/campaign/:id',
        element: <Campaign />
      },
      {
        path: '/mission-planner',
        element: <MissionPlanner />
      },

      {
        path: '/devices',
        element: <Devices />
      },

      {
        path: '/organization-settings',
        element: <OrganizationSettings />
      },
      {
        path: '/device-types',
        element: <DeviceTypeList />
      },
      {
        path: '/system-devices',
        element: <DeviceList />
      },
      {
        path: '/device-details/:deviceId',
        element: <DeviceDetails />
      },
      ...pilotRoutes
    ]
  },
  {
    path: '*',
    element: <NotFound />
  }
];

const AppRoutes: FC = () => {
  const routing = useRoutes(routes);
  return routing;
};

export default AppRoutes;
